import React, { createContext, FC, ReactNode, useCallback, useContext, useMemo, useState } from "react";
import { GuardianForm } from "../models/api-models/guardians/guardian-form";
import { GuardianFormSubmissionParsed } from "../models/api-models/guardians/guardian-form-submission";

export interface GuardianContextModel {
    formSubmission: GuardianFormSubmissionParsed,
    form: GuardianForm,
    signature: string | undefined,
    setData: (data: {form: GuardianForm, submission: GuardianFormSubmissionParsed}) => void,
    updateSignature: (signature: string | undefined) => void,
    setIsLoad: (value: boolean) => void;
    loading: boolean,
    partialUpdateFormSubmission: (value: Partial<GuardianFormSubmissionParsed>) => void
}

interface GuardianProviderProps {
  children: ReactNode;
}

const GuardianContext = createContext<GuardianContextModel | undefined>(undefined);

export const GuardianProvider: FC<GuardianProviderProps> = ({children}) => {

    const [formSubmission, setFormSubmission] = useState<GuardianFormSubmissionParsed>();
    const [form, setForm] = useState<GuardianForm>();
    const [signature, setSignature] = useState<string | undefined>();
    const [loading, setLoading] = useState(false);

    const setData = useCallback(({ form, submission }: {form: GuardianForm, submission: GuardianFormSubmissionParsed}) => {
        setForm(form);
        setFormSubmission(submission)
    }, [])

    const updateSignature = useCallback((signature: string | undefined) => {
        setSignature(signature);
    }, [])

    const setIsLoad = useCallback((value: boolean) => {
        setLoading(value);
    }, []);

    const partialUpdateFormSubmission = useCallback((value: Partial<GuardianFormSubmissionParsed>) => {
        setFormSubmission(submission => ({...submission, value }));
    }, []);
    

    const value = useMemo<GuardianContextModel>(() => ({
        formSubmission,
        form,
        setData,
        signature, 
        updateSignature,
        loading,
        setIsLoad,
        partialUpdateFormSubmission
    }), 
    [
        formSubmission, 
        form, 
        signature,
        setData,
        updateSignature,
        loading,
        setIsLoad,
        partialUpdateFormSubmission
    ]);
        

    return (
        <GuardianContext.Provider value={value}>
            {children}
        </GuardianContext.Provider>
    );
}


export const useGuardianContext = () => {
    const context = useContext(GuardianContext);
    if(!context) {
        throw new Error("useGuardianContext must be used within a GuardianProvider");
    }
    return context
}